/**
 * useTheme - 主题切换 composable
 * 参考 design/context/ThemeContext.tsx
 *
 * 用法:
 *   import { useTheme } from '@saas/components/src/composables/useTheme'
 *   const { theme, isDark, toggleTheme } = useTheme()
 */

let _theme = null; // 单例，保证全局同步
let _listeners = [];

function getInitialTheme() {
  if (process.env.VUE_APP_BUILD_ENV === 'production') {
    return 'light';
  }
  // 临时关闭
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') return stored;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  }
  return 'light';
}

function applyTheme(theme) {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  localStorage.setItem('theme', theme);
}

/**
 * 获取当前主题
 * @returns {'light'|'dark'}
 */
export function getTheme() {
  if (_theme === null) {
    _theme = getInitialTheme();
    applyTheme(_theme);
  }
  return _theme;
}

/**
 * 切换主题
 */
export function toggleTheme() {
  _theme = _theme === 'light' ? 'dark' : 'light';
  applyTheme(_theme);
  _listeners.forEach(fn => fn(_theme));
}

/**
 * 设置指定主题
 * @param {'light'|'dark'} theme
 */
export function setTheme(theme) {
  if (theme !== 'light' && theme !== 'dark') return;
  _theme = theme;
  applyTheme(_theme);
  _listeners.forEach(fn => fn(_theme));
}

/**
 * 监听主题变化
 * @param {Function} fn
 * @returns {Function} unsubscribe
 */
export function onThemeChange(fn) {
  _listeners.push(fn);
  return () => {
    _listeners = _listeners.filter(l => l !== fn);
  };
}

/**
 * Vue 2 mixin（适用于 Options API）
 */
export const themeMixin = {
  data() {
    return {
      currentTheme: getTheme(),
    };
  },
  computed: {
    isDark() {
      return this.currentTheme === 'dark';
    },
  },
  created() {
    this._unsubTheme = onThemeChange((theme) => {
      this.currentTheme = theme;
    });
  },
  beforeDestroy() {
    if (this._unsubTheme) {
      this._unsubTheme();
    }
  },
  methods: {
    toggleTheme,
    setTheme,
  },
};

// 初始化：监听系统偏好变化
if (typeof window !== 'undefined' && window.matchMedia) {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', (e) => {
    // 仅在用户未手动设置时跟随系统
    const stored = localStorage.getItem('theme');
    if (!stored) {
      setTheme(e.matches ? 'dark' : 'light');
    }
  });
}

// 立即初始化
getTheme();
